"use client";
import { NavigationMenuLink } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { Fragment, useState } from "react";
import type { CategoriesPanel } from "../../data";
import RingBulletItem from "@/components/common/ringBulletItem/ringBulletItem.component";
import ViewAllLink from "../viewAllLink/viewAllLink.component";
import IconTile from "@/components/common/iconTile/iconTile.component";

interface CategoriesMenuPanelProps {
  panel: CategoriesPanel;
  pathname: string;
  // Keeps the detail-panel ids unique when two panels are mounted at once.
  idPrefix: string;
}

const linkBase =
  "hover:bg-paper hover:text-ink focus:bg-paper focus:text-ink data-[active=true]:bg-paper data-[active=true]:hover:bg-paper data-[active=true]:focus:bg-paper";

// Desktop Solutions/Resources mega menu: categories on the left, the hovered
// or focused category's detail on the right. Each detail is rendered right
// after its own row (and placed into the right column by the grid), so Tab
// moves from a category straight into its links instead of through every
// other category first.
const CategoriesMenuPanel = ({
  panel,
  pathname,
  idPrefix,
}: CategoriesMenuPanelProps) => {
  const initialState = { activeIndex: 0 };
  const [state, setState] = useState(initialState);

  const activate = (activeIndex: number) =>
    setState((state) => ({ ...state, activeIndex }));

  return (
    // row-span-12 on each detail spans the whole left column; the unused
    // implicit rows collapse to zero height since there's no row gap.
    <div className="grid grid-cols-[5fr_7fr] py-5">
      <span className="col-start-1 row-start-1 mr-5 mb-3 ml-8 block text-sm font-semibold text-ink-muted">
        {panel.sectionLabel}
      </span>

      {panel.categories.map((category, index) => {
        const isActive = state.activeIndex === index;
        const detailId = `${idPrefix}-${index}`;
        const rowClass = cn(
          "col-start-1 mr-4 ml-5 grid grid-cols-[44px_1fr] items-center gap-3.5 rounded-xl px-3 py-2.5 text-left text-ink",
          isActive && "bg-paper",
        );
        const rowContent = (
          <>
            <IconTile icon={category.icon} index={index} />
            <span>
              <span
                className={cn(
                  "block text-base leading-tight font-semibold",
                  isActive && "text-primary",
                )}
              >
                {category.label}
              </span>
              <span className="mt-0.5 block text-[13.5px] leading-snug text-ink-muted">
                {category.description}
              </span>
            </span>
          </>
        );

        return (
          <Fragment key={category.label}>
            {category.route ? (
              <NavigationMenuLink
                asChild
                active={pathname === category.route}
                className={cn(linkBase, rowClass)}
                onMouseEnter={() => activate(index)}
                onFocus={() => activate(index)}
              >
                <Link href={category.route}>{rowContent}</Link>
              </NavigationMenuLink>
            ) : (
              // No hub page yet - the row only switches the detail view.
              <button
                type="button"
                aria-expanded={isActive}
                aria-controls={detailId}
                className={cn(rowClass, "cursor-pointer hover:bg-paper")}
                onMouseEnter={() => activate(index)}
                onFocus={() => activate(index)}
                onClick={() => activate(index)}
              >
                {rowContent}
              </button>
            )}

            <div
              id={detailId}
              role="group"
              aria-label={category.label}
              className={cn(
                "col-start-2 row-span-12 row-start-1 content-start border-l border-line px-7 pb-2",
                isActive ? "block" : "hidden",
              )}
            >
              <span className="mb-3 ml-2.5 block text-sm font-semibold text-ink-muted">
                {category.label}
              </span>

              {category.links && (
                <ul className="grid grid-cols-2 gap-x-2 gap-y-1">
                  {category.links.map((link, linkIndex) => (
                    <li key={link.route}>
                      <NavigationMenuLink
                        asChild
                        active={pathname === link.route}
                        className={cn(
                          linkBase,
                          "flex-row items-center gap-3 rounded-xl px-2.5 py-2 text-base leading-tight font-medium text-ink data-[active=true]:font-semibold data-[active=true]:text-primary",
                        )}
                      >
                        <Link href={link.route}>
                          {link.icon && (
                            <IconTile
                              icon={link.icon}
                              index={linkIndex}
                              size="sm"
                            />
                          )}
                          {link.label}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              )}

              {(category.posts || category.notes) && (
                <>
                  <span className="mb-3 ml-2.5 block max-w-[46ch] text-[15px] text-ink-muted">
                    {category.description}
                  </span>
                  <ul className="ml-2.5 grid gap-2">
                    {category.posts?.map((post) => (
                      <RingBulletItem key={post.route}>
                        <NavigationMenuLink
                          asChild
                          className="inline p-0 text-[15px] leading-snug font-medium text-ink hover:bg-transparent hover:text-primary hover:underline focus:bg-transparent focus:text-primary"
                        >
                          <Link href={post.route}>{post.label}</Link>
                        </NavigationMenuLink>
                      </RingBulletItem>
                    ))}
                    {category.notes?.map((note) => (
                      <RingBulletItem
                        key={note}
                        className="text-[15px] leading-snug text-ink"
                      >
                        {note}
                      </RingBulletItem>
                    ))}
                  </ul>
                </>
              )}

              {category.route && category.exploreLabel && (
                <NavigationMenuLink
                  asChild
                  className="mt-3.5 ml-2.5 inline-block p-0 text-[15px] font-semibold text-primary underline underline-offset-[3px] hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary"
                >
                  <Link href={category.route}>{category.exploreLabel}</Link>
                </NavigationMenuLink>
              )}
            </div>
          </Fragment>
        );
      })}

      {panel.allLink && (
        <ViewAllLink
          link={panel.allLink}
          isMenuLink
          className="col-start-1 mx-5 mt-2"
        />
      )}
      {panel.footerLink && (
        <NavigationMenuLink
          asChild
          className="col-start-1 mx-5 mt-2.5 flex-row items-center justify-between gap-2.5 rounded-2xl promo-band px-3.5 py-2.5 text-[14.5px] font-bold text-ink hover:bg-tone-peach hover:bg-none hover:text-primary focus:bg-tone-peach focus:bg-none focus:text-primary"
        >
          <Link href={panel.footerLink.route}>
            {panel.footerLink.label}
            <ArrowRight className="size-5 text-primary" aria-hidden="true" />
          </Link>
        </NavigationMenuLink>
      )}
    </div>
  );
};

export default CategoriesMenuPanel;
